import { useEffect } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/outline";
import { LockClosedIcon } from "@heroicons/react/solid";
import { RiVipCrown2Line } from "@remixicon/react";
import InputField from "components/InputField";
import Select from "components/Select";
import Button from "components/Button";
import { cx } from "utils";
import { PureTooltip, Tooltip } from "components/Tooltip";
import NextJobsDisclosure from "./NextJobsDisclosure";
import { CRON_PRESETS, getFrequencyOptions, getScheduleDisplayName, getScheduleFromDisplayName } from "pages/crawler/utils/crawlerFormUtils";
import { OutputFormatSelect } from "./OutputFormatSelect";
import { useUser } from "routes/dataroutes/UserData";
import { useCrawlerCostContext } from "pages/crawler/providers/CrawlerCostProvider";

import { canUsePremium } from "utils/planUtils";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";


const CrawlerOptionsDisclosure = ({
  register,
  watchedValues,
  setValue,
  testCallback,
  errors,
  isTestingCallback = false,
  callbackTestResult = null,
  setCallbackTestResult = null,
}: any) => {
  const user = useUser();
  const { activeSubscription } = useActiveSubscription();
  const isFreePlan = !!activeSubscription && !canUsePremium(activeSubscription.planSlug);

  // Force free plan defaults once subscription loads
  useEffect(() => {
    if (isFreePlan) {
      setValue("linkDepth", Math.min(1, watchedValues.linkDepth), { shouldValidate: true });
      const onceSchedule = getScheduleFromDisplayName(CRON_PRESETS.once.label);
      setValue("schedule", onceSchedule, { shouldDirty: true, shouldValidate: true });
    }
  }, [isFreePlan]); // eslint-disable-line react-hooks/exhaustive-deps

  const creditsLeft = user ? user.creditsRemaining : 0;

  // Shared crawler cost estimate (used by the max-cost validation below).
  const { costPerRequest } = useCrawlerCostContext();

  return (
  <Disclosure defaultOpen>
    {({ open }) => (
      <>
        <DisclosureButton
          className={cx(
            "flex flex-row items-center w-full transition-colors duration-50 p-4"
          )}
        >
          {open ? (
            <ChevronUpIcon className="h-4 w-4 mr-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 mr-4" />
          )}
          Crawler Options
        </DisclosureButton>

        <DisclosurePanel className="flex flex-col p-4" style={{ marginTop: 0 }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Link Depth */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <Tooltip content="How many levels of links to follow from the starting page. Higher values crawl more pages but cost more credits.">
                  Link depth <span className="text-red-500">*</span>
                </Tooltip>
              </label>
              <InputField
                {...register("linkDepth", {
                  valueAsNumber: true,
                  validate: (value: number) => {
                    // No negative values
                    if (value < 0) {
                      return "Link depth cannot be negative";
                    } else if (isFreePlan && 1 < value) {
                      return "Link depth greater than 1 is not available on the Free plan. Please upgrade to configure deeper crawls.";
                    }

                    // Check if maxCost is set and > 0
                    const maxCost = watchedValues.maxCost;
                    const maxCostNum = maxCost ? parseInt(maxCost) : 0;
                    const hasValidMaxCost = maxCostNum > 0;

                    // If no valid maxCost, linkDepth must be >= 1
                    if (!hasValidMaxCost && (!value || value < 1)) {
                      return "Link depth must be at least 1, or set a Maximum Cost";
                    }

                    // If both are 0/empty, show error
                    if ((!value || value === 0) && !hasValidMaxCost) {
                      return "Either Link Depth or Maximum Cost must be set";
                    }

                    return true;
                  }
                })}
                type="number"
                placeholder="e.g. 2"
                value={watchedValues.linkDepth}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value);
                  setValue("linkDepth", newValue, { shouldValidate: true });
                  // Always re-validate maxCost since the validation depends on both fields
                  setTimeout(() => {
                    setValue("maxCost", watchedValues.maxCost, { shouldValidate: true });
                  }, 0);
                }}
                TrailingElm={isFreePlan ? (
                  <PureTooltip content="Link depth is limited to 1 on the Free plan. Please upgrade to configure deeper crawls." clickable>
                    <LockClosedIcon className="w-4 h-4 text-gray-500 opacity-50" />
                  </PureTooltip>
                ) : (
                  <PureTooltip content="This feature is available with a paid plan.">
                    <span onMouseDown={(e) => e.preventDefault()}><RiVipCrown2Line className="w-4 h-4 text-gray-500 opacity-50" /></span>
                  </PureTooltip>
                )}
              />
              {errors?.linkDepth && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.linkDepth.message}
                </p>
              )}
            </div>

            {/* Empty div to take up second column, pushing other fields to next row */}
            <div></div>
            {/* Include Path */}
            <div>
              <div className="flex justify-between">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                  <Tooltip content="Regex pattern to match URLs that should be crawled. Only URLs matching this pattern will be followed. Use .* to crawl all pages on the site.">
                    Include path <span className="text-red-500">*</span>
                  </Tooltip>
                </label>
                <a
                  href="https://docs.scraperapi.com/scraperapi-crawler-v2.0/crawler-ui#crawler-options"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-rebrandBlue-600 hover:underline"
                >
                  Documentation
                </a>
              </div>
              <InputField
                {...register("includePath", {
                  required: "URL pattern is required - this determines which URLs to crawl",
                  validate: (value: string) => {
                    if (!value || !value.trim()) return "URL pattern is required - this determines which URLs to crawl";
                    try {
                      new RegExp(value);
                      return true;
                    } catch (e) {
                      return "Invalid URL pattern syntax. Example: .* (match all), /product/.* (match URLs with '/product/')";
                    }
                  },
                })}
                placeholder="e.g. .* or /product/.*"
                value={watchedValues.includePath}
                onChange={(e) =>
                  setValue("includePath", e.target.value, {
                    shouldValidate: true,
                  })
                }
              />
              {errors?.includePath && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.includePath.message}
                </p>
              )}
            </div>

            {/* Exclude Path */}
            <div>
              <div className="flex justify-between">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                  <Tooltip content="Regex pattern to exclude URLs from crawling. URLs matching this pattern will be skipped. Leave empty to crawl all matched URLs.">
                    Exclude path
                  </Tooltip>
                </label>
                <a
                // want to make this dynamic - call from gitbook in server
                  href="https://docs.scraperapi.com/scraperapi-crawler-v2.0/crawler-ui#crawler-options"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-rebrandBlue-600 hover:underline"
                >
                  Documentation
                </a>
              </div>
              <InputField
                {...register("excludePath", {
                  validate: (value: string) => {
                    if (!value || !value.trim()) return true; // Optional field
                    try {
                      new RegExp(value);
                      return true;
                    } catch (e) {
                      return "Invalid URL pattern syntax. Example pattern: /admin/.* (this would exclude any URL containing '/admin/')";
                    }
                  },
                })}
                placeholder="e.g. .*/product/.*"
                value={watchedValues.excludePath}
                onChange={(e) =>
                  setValue("excludePath", e.target.value, {
                    shouldValidate: true,
                  })
                }
              />
              {errors?.excludePath && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.excludePath.message}
                </p>
              )}
            </div>

            {/* Callback URL */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <Tooltip content="Webhook URL to receive notifications when the crawler finishes. Your endpoint will receive a POST request with the results.">
                  Callback URL
                </Tooltip>
              </label>
              <div className="flex gap-2 items-stretch">
                <div className="w-full">
                  <InputField
                    {...register("callbackUrl", {
                      validate: (value: string) => {
                        if (!value?.trim()) return true; // Callback URL is optional

                        // Check if URL starts with http:// or https://
                        if (!value.startsWith('http://') && !value.startsWith('https://')) {
                          return "URL must start with http:// or https://";
                        }

                        try {
                          const url = new URL(value);

                          // Ensure there's a hostname
                          if (!url.hostname || url.hostname.length === 0) {
                            return "Please provide a valid URL";
                          }

                          // Check for valid domain structure (must have at least one dot or be localhost/IP)
                          const hostname = url.hostname.toLowerCase();
                          const isLocalhost = hostname === 'localhost' || hostname.startsWith('127.');
                          const isIP = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
                          const hasTLD = hostname.includes('.') && hostname.split('.').length >= 2 && hostname.split('.').pop()!.length >= 2;

                          if (!isLocalhost && !isIP && !hasTLD) {
                            return "Please provide a complete URL (e.g., https://webhook.site/your-id)";
                          }

                          return true;
                        } catch {
                          return "Please provide a valid URL";
                        }
                      }
                    })}
                    type="url"
                    placeholder="e.g. https://your-webhook.com/callback"
                    value={watchedValues.callbackUrl}
                    onChange={(e) => {
                      setValue("callbackUrl", e.target.value);
                      // Clear test result when URL changes to avoid showing stale status
                      if (
                        callbackTestResult &&
                        typeof setCallbackTestResult === "function"
                      ) {
                        setCallbackTestResult(null);
                      }
                    }}
                    className="flex-1"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <Button
                    text={isTestingCallback ? "TESTING..." : "TEST"}
                    onClick={testCallback}
                    disabled={isTestingCallback}
                    className="button button-secondary h-11"
                  />
                  {callbackTestResult && (
                    <span
                      className={cx(
                        "text-xs text-center",
                        callbackTestResult.status === "success"
                          ? "text-green-600"
                          : "text-red-600"
                      )}
                    >
                      {callbackTestResult.message}
                    </span>
                  )}
                </div>
              </div>
              {errors?.callbackUrl && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.callbackUrl.message}
                </p>
              )}
            </div>

            {/* Maximum Cost */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <Tooltip content="Stop the crawler when this number of credits is reached. Helps prevent unexpected high costs.">
                  Maximum cost in credits <span className="text-red-500">*</span>
                </Tooltip>
              </label>
              <InputField
                {...register("maxCost", {
                  validate: (value: string) => {
                    const maxCostNum = value ? parseInt(value) : 0;

                    // No negative values
                    if (maxCostNum < 0) {
                      return "Maximum cost cannot be negative";
                    }

                    // Check if linkDepth is set and >= 1
                    const linkDepth = watchedValues.linkDepth;
                    const hasValidLinkDepth = linkDepth && linkDepth >= 1;

                    // If no valid linkDepth, maxCost must be > 0
                    if (!hasValidLinkDepth && (!value || value.trim() === "" || maxCostNum === 0)) {
                      return "Maximum cost must be greater than 0, or set a Link Depth";
                    }

                    // If both are 0/empty, show error
                    if ((!value || value.trim() === "" || maxCostNum === 0) && !hasValidLinkDepth) {
                      return "Either Link Depth or Maximum Cost must be set";
                    }

                    // Check if maxCost is higher than user's current credit balance
                    if (maxCostNum > 0 && maxCostNum > creditsLeft) {
                      return `Your current balance is lower than this budget. Reduce the max credits or add more credits.`;
                    }

                    // Check if maxCost is less than the calculated cost per request
                    if (maxCostNum > 0 && costPerRequest > 1 && maxCostNum < costPerRequest) {
                      return `Maximum budget (${maxCostNum} credits) is lower than the estimated cost per request (${costPerRequest} credits). Increase the budget or adjust your settings.`;
                    }

                    return true;
                  }
                })}
                type="number"
                placeholder="e.g. 1000"
                value={watchedValues.maxCost}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setValue("maxCost", newValue, { shouldValidate: true });
                  // Always re-validate linkDepth since the validation depends on both fields
                  setTimeout(() => {
                    setValue("linkDepth", watchedValues.linkDepth, { shouldValidate: true });
                  }, 0);
                }}
              />
              {errors?.maxCost && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.maxCost.message}
                </p>
              )}
            </div>

            {/* Frequency */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <Tooltip content="How often the crawler should run automatically. 'Scrape Once' runs one time immediately. 'Scheduling Disabled' prevents automatic scheduling.">
                Crawling frequency
                </Tooltip>
              </label>
              <Select
                name="schedule"
                value={getScheduleDisplayName(watchedValues.schedule)}
                options={getFrequencyOptions()}
                locked={isFreePlan}
                tooltip={isFreePlan
                  ? "Scheduled crawls are not available on the Free plan. Please upgrade to enable scheduling."
                  : "This feature is available with a paid plan."
                }
                premium={!isFreePlan}
                onChange={(value) => {
                  const nextSchedule = getScheduleFromDisplayName(value as string);
                  setValue("schedule", nextSchedule, { shouldDirty: true, shouldValidate: true });
                }}
              />
              <NextJobsDisclosure schedule={watchedValues.schedule} />
            </div>

            {/* Output Format */}
            <OutputFormatSelect
              outputFormat={watchedValues.outputFormat}
              autoparse={watchedValues.autoparse}
              onChange={(format, autoparse) => {
                setValue('outputFormat', format, { shouldDirty: true });
                setValue('autoparse', autoparse, { shouldDirty: true });
              }}
            />
          </div>
        </DisclosurePanel>
      </>
    )}
  </Disclosure>
  );
};

export default CrawlerOptionsDisclosure;
